import React, { useEffect, useState } from 'react';
import { CircleStackIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { collection, query, where, orderBy, limit, onSnapshot, getDocs } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';

function formatCurrency(amountCents, currency = 'USD') {
  const amt = typeof amountCents === 'number' ? amountCents / 100 : 0;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: String(currency || 'USD').toUpperCase() }).format(amt);
  } catch (e) {
    return '$' + amt.toFixed(2);
  }
}

export default function PurchaseHistoryPage() {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | credit_purchase | print_job

  useEffect(() => {
    if (!auth.currentUser) {
      setRows([]);
      setIsLoading(false);
      return;
    }

    let unsubscribePurchases = () => {};
    let unsubscribeCredits = () => {};
    let unsubscribePrintJobs = () => {};

    const combineAndSet = (purchasesList, creditsList, printJobsList = []) => {
      const purchaseRows = purchasesList.map((p) => ({
        id: p.id,
        printJobId: p.printJobId || null,
        type: p.type || 'print_job',
        status: p.status || 'completed',
        createdAt: (p.createdAt && p.createdAt.toDate ? p.createdAt.toDate() : p.createdAt) || null,
        receiptNumber: p.receiptNumber || 'Receipt #' + p.id,
        credits: null,
        amountCents: typeof p.amount === 'number' ? p.amount : (p.amountCents || p.totalAmount || 0),
        currency: (p.currency || 'USD').toUpperCase(),
      }));

      const creditRows = creditsList.map((t) => ({
        id: t.id,
        type: 'credit_purchase',
        status: t.status || 'completed',
        createdAt: (t.timestamp && t.timestamp.toDate ? t.timestamp.toDate() : t.timestamp) || null,
        receiptNumber: 'Receipt #' + t.id,
        credits: t.amount || 0,
        amountCents: typeof t.price === 'number' ? Math.round(t.price * 100) : 0,
        currency: (t.currency || 'USD').toUpperCase(),
      }));

      const isPaidPrintJob = (j) => Boolean(
        j?.stripePaid === true ||
        j?.customerPaymentStatus === 'paid' ||
        j?.paidAt ||
        (typeof j?.paymentStatus === 'string' && j.paymentStatus.startsWith('PAID'))
      );

      const printRowsRaw = (printJobsList || []).filter(isPaidPrintJob).map((j) => ({
        id: j.printJobId || j.id,
        printJobId: j.printJobId || j.id,
        type: 'print_job',
        status: j.customerPaymentStatus || j.paymentStatus || 'completed',
        createdAt: ((j.paidAt && j.paidAt.toDate ? j.paidAt.toDate() : j.paidAt) ||
                    (j.updatedAt && j.updatedAt.toDate ? j.updatedAt.toDate() : j.updatedAt) ||
                    null),
        receiptNumber: 'Receipt #' + (j.printJobId || j.id),
        credits: null,
        amountCents: (typeof j.amountTotal === 'number' ? j.amountTotal : (j.totalAmount || 0)),
        currency: (j.currency || 'USD').toUpperCase(),
      }));

      const purchasePrintIds = new Set(purchaseRows.map(r => r.type === 'print_job' ? (r.printJobId || r.id) : null).filter(Boolean));
      const printRows = printRowsRaw.filter(r => !purchasePrintIds.has(r.printJobId));

      const combined = [...purchaseRows, ...creditRows, ...printRows];
      combined.sort((a, b) => {
        const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return tb - ta;
      });
      setRows(combined);
      setIsLoading(false);
      setError(null);
    };

    const load = async () => {
      try {
        const pq = query(
          collection(db, 'purchases'),
          where('userId', '==', auth.currentUser.uid),
          orderBy('createdAt', 'desc'),
          limit(100)
        );
        const tq = query(
          collection(db, 'transactions'),
          where('userId', '==', auth.currentUser.uid),
          where('action', '==', 'credit_purchase'),
          orderBy('timestamp', 'desc'),
          limit(100)
        );
        const qj = query(
          collection(db, 'printJobs'),
          where('userId', '==', auth.currentUser.uid),
          where('stripePaid', '==', true),
          limit(100)
        );

        let latestPurchases = [];
        let latestCredits = [];
        let latestPrintJobs = [];

        try {
          unsubscribePurchases = onSnapshot(
            pq,
            (snap) => {
              latestPurchases = snap.docs.map((d) => ({ id: d.id, ...d.data() })) || [];
              combineAndSet(latestPurchases, latestCredits, latestPrintJobs);
            },
            async (err) => {
              if (err && err.code === 'failed-precondition') {
                const fbQ = query(
                  collection(db, 'purchases'),
                  where('userId', '==', auth.currentUser.uid),
                  limit(100)
                );
                const ss = await getDocs(fbQ);
                latestPurchases = ss.docs.map((d) => ({ id: d.id, ...d.data() })) || [];
                combineAndSet(latestPurchases, latestCredits, latestPrintJobs);
              } else {
                console.error('Purchases listener error:', err);
                setError('Failed to load purchases.');
                setIsLoading(false);
              }
            }
          );
        } catch (e) {
          if (e && e.code === 'failed-precondition') {
            const fbQ = query(collection(db, 'purchases'), where('userId', '==', auth.currentUser.uid), limit(100));
            const ss = await getDocs(fbQ);
            latestPurchases = ss.docs.map((d) => ({ id: d.id, ...d.data() })) || [];
            combineAndSet(latestPurchases, latestCredits);
          } else {
            throw e;
          }
        }

        try {
          unsubscribeCredits = onSnapshot(
            tq,
            (snap) => {
              latestCredits = snap.docs.map((d) => ({ id: d.id, ...d.data() })) || [];
              combineAndSet(latestPurchases, latestCredits, latestPrintJobs);
            },
            async (err) => {
              if (err && err.code === 'failed-precondition') {
                const fbQ = query(
                  collection(db, 'transactions'),
                  where('userId', '==', auth.currentUser.uid),
                  where('action', '==', 'credit_purchase'),
                  limit(100)
                );
                const ss = await getDocs(fbQ);
                latestCredits = ss.docs.map((d) => ({ id: d.id, ...d.data() })) || [];
                combineAndSet(latestPurchases, latestCredits, latestPrintJobs);
              } else {
                console.error('Credits listener error:', err);
                setError('Failed to load credit purchases.');
                setIsLoading(false);
              }
            }
          );

        // Print jobs listener (fallback loads and client-side paid filtering handled in combine)
        try {
          unsubscribePrintJobs = onSnapshot(
            qj,
            (snap) => {
              latestPrintJobs = snap.docs.map((d) => ({ id: d.id, ...d.data() })) || [];
              combineAndSet(latestPurchases, latestCredits, latestPrintJobs);
            },
            async () => {
              const fbQ = query(
                collection(db, 'printJobs'),
                where('userId', '==', auth.currentUser.uid),
                limit(100)
              );
              const ss = await getDocs(fbQ);
              latestPrintJobs = ss.docs.map((d) => ({ id: d.id, ...d.data() })) || [];
              combineAndSet(latestPurchases, latestCredits, latestPrintJobs);
            }
          );
        } catch (e) {
          const fbQ = query(collection(db, 'printJobs'), where('userId', '==', auth.currentUser.uid), limit(100));
          const ss = await getDocs(fbQ);
          latestPrintJobs = ss.docs.map((d) => ({ id: d.id, ...d.data() })) || [];
          combineAndSet(latestPurchases, latestCredits, latestPrintJobs);
        }
        } catch (e) {
          if (e && e.code === 'failed-precondition') {
            const fbQ = query(collection(db, 'transactions'), where('userId', '==', auth.currentUser.uid), where('action', '==', 'credit_purchase'), limit(100));
            const ss = await getDocs(fbQ);
            latestCredits = ss.docs.map((d) => ({ id: d.id, ...d.data() })) || [];
            combineAndSet(latestPurchases, latestCredits);
          } else {
            throw e;
          }
        }
      } catch (e) {
        console.error('Purchase history setup error:', e);
        setError('Failed to load purchase history.');
        setIsLoading(false);
      }
    };

    load();
    return () => {
      try { unsubscribePurchases(); } catch (e) {}
      try { unsubscribeCredits(); } catch (e) {}
      try { unsubscribePrintJobs(); } catch (e) {}
    };
  }, []);

  const filtered = rows.filter((r) => (filter === 'all' ? true : r.type === filter));

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-700">{error}</p>
          <button onClick={() => window.location.reload()} className="mt-2 text-sm text-red-600 hover:text-red-500">Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Purchase History</h1>
        <select
          className="bg-white text-gray-900 rounded-lg px-4 py-2 border border-gray-300"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="credit_purchase">Credit Purchase</option>
          <option value="print_job">Print Order</option>
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="text-left text-gray-500 text-sm bg-gray-50 border-b border-gray-200">
              <th className="px-6 py-4 font-medium">Receipt</th>
              <th className="px-6 py-4 font-medium">Type</th>
              <th className="px-6 py-4 font-medium">Date</th>
              <th className="px-6 py-4 font-medium">Status</th>
              <th className="px-6 py-4 font-medium">Amount</th>
              <th className="px-6 py-4 font-medium">Credits</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map((item) => (
              <tr key={item.id}>
                <td className="px-6 py-4 text-gray-900">
                  {(item.receiptNumber || `Receipt #${item.id}`)} - {format(item.createdAt || new Date(), 'MMM yyyy')}
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {item.type}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-500">{format(item.createdAt || new Date(), 'MM/dd/yyyy')}</td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {item.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-900">{formatCurrency(item.amountCents || 0, item.currency)}</td>
                <td className="px-6 py-4">
                  <span className={item.type === 'credit_purchase' ? 'text-green-600' : 'text-gray-500'}>
                    <CircleStackIcon className="inline-block h-4 w-4 mr-1" />
                    {item.type === 'credit_purchase' ? ('+' + String(item.credits)) : '—'}
                  </span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500">No purchases yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
